/* PDPPL v2.0 renewal banner — sticky top-of-app prompt.
 *
 * Fetches /api/admin/legal/pdppl-status on every admin/portal mount. If the
 * current tenant's most recent consent is on an older PDPPL version, render a
 * compliance banner pinned to the top with a "Review and accept" button that
 * opens the full text + a confirm action.
 *
 * Renders nothing for non-authed users or up-to-date tenants.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, X, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePdpplPolicy } from "@/components/PrivacyConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "pdppl_renewal_dismissed_until";

export default function PdpplRenewalBanner() {
    const { user } = useAuth();
    const policy = usePdpplPolicy();
    const [status, setStatus] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!user) return;
        // Honour a soft-dismiss (24h cooldown) so the banner doesn't nag on every page hop.
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw && Number(raw) > Date.now()) {
                setDismissed(true);
                return;
            }
        } catch (e) { /* ignore */ }
        axios.get(`${API}/admin/legal/pdppl-status`).then(({ data }) => {
            setStatus(data);
        }).catch(() => { /* silent — banner just won't show */ });
    }, [user]);

    if (!user || dismissed || !status?.needs_renewal) return null;

    const softDismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
        } catch (e) { /* ignore */ }
        setDismissed(true);
    };

    const accept = async () => {
        if (!agreed) {
            toast.error("Tick the acceptance box before continuing.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/admin/legal/pdppl-renew`, { confirm: true });
            toast.success(`Thanks — your tenant is now on PDPPL v${status.current_version}.`);
            setOpen(false);
            setStatus({ ...status, needs_renewal: false });
            try { localStorage.removeItem(DISMISS_KEY); } catch (e) { /* ignore */ }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to record acceptance");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div data-testid="pdppl-renewal-banner" className="relative bg-amber-50 border-b border-amber-200">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-2.5 flex items-center gap-4 flex-wrap">
                    <ShieldAlert size={16} className="text-amber-700 shrink-0" />
                    <div className="text-[13px] text-amber-900 leading-snug flex-1 min-w-[200px]">
                        <span className="font-semibold">PDPPL v{status.current_version} is now live.</span>{" "}
                        Your tenant is still on v{status.tenant_version || "—"}. The new version adds explicit alignment with <strong>ISO/IEC 27001, ISO 13485, GDPR, WHO Patient Safety</strong>, and <strong>ICH-GCP</strong>.
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setOpen(true)} data-testid="pdppl-renewal-review" className="px-3.5 py-1.5 rounded-lg bg-amber-700 text-white text-[12.5px] font-medium hover:bg-amber-800">
                            Review & accept v{status.current_version}
                        </button>
                        <button onClick={softDismiss} aria-label="Dismiss for 24h" data-testid="pdppl-renewal-dismiss" className="p-1.5 rounded-lg text-amber-700 hover:bg-amber-100">
                            <X size={14} />
                        </button>
                    </div>
                </div>
            </div>

            {open && policy && (
                <div className="fixed inset-0 z-[70] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center pt-[5vh] px-4 overflow-y-auto" data-testid="pdppl-renewal-modal">
                    <div className="relative bg-white rounded-2xl w-full max-w-3xl p-8 shadow-2xl my-6">
                        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" aria-label="Close"><X size={18} /></button>
                        <div className="flex items-center gap-2 mb-3">
                            <ShieldAlert size={16} className="text-amber-700" />
                            <span className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">
                                · From v{status.tenant_version || "0.0"} → v{status.current_version}
                            </span>
                        </div>
                        <h2 className="font-display text-2xl text-slate-900 tracking-tight mb-3">Re-accept the updated PDPPL terms</h2>
                        <p className="text-[13.5px] text-slate-600 leading-relaxed mb-4">
                            We updated our Personal Data Privacy commitments to make explicit the international standards we already follow. The liability allocation between you (Data Controller) and Toothmatrix (Data Processor) <strong>has not changed</strong>; we are simply naming the standards.
                        </p>
                        <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4 max-h-[40vh] overflow-y-auto text-[13px] leading-relaxed space-y-3">
                            {(policy.full_text_markdown || "").split(/\n\n+/).map((b) => {
                                const t = b.trim();
                                // Static, idempotent policy markdown — use a stable content
                                // prefix as the React key (avoids array-index anti-pattern).
                                const blockKey = `bor-${t.slice(0, 48)}`;
                                if (t.startsWith("**") && t.endsWith("**")) {
                                    return <h3 key={blockKey} className="font-display text-[15px] text-slate-900 mt-3">{t.replace(/\*\*/g, "")}</h3>;
                                }
                                if (t.startsWith("- ")) {
                                    const items = t.split(/\n- /).map((x) => x.replace(/^- /, ""));
                                    return <ul key={blockKey} className="list-disc pl-5 space-y-1 text-slate-700">{items.map((it) => <li key={`li-${it.slice(0, 64)}`} dangerouslySetInnerHTML={{ __html: it.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />)}</ul>;
                                }
                                return <p key={blockKey} className="text-slate-700" dangerouslySetInnerHTML={{ __html: t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />;
                            })}
                        </div>
                        <label className="mt-5 flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" data-testid="pdppl-renewal-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-400 text-cyan-600 focus:ring-cyan-500" />
                            <span className="text-[13px] text-slate-700">
                                I confirm I have read v{status.current_version} and accept it on behalf of my tenant. I sign as <strong>{user?.email}</strong>; my IP and user agent will be captured for audit.
                            </span>
                        </label>
                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
                            <button onClick={accept} disabled={!agreed || busy} data-testid="pdppl-renewal-accept" className="px-4 py-2 rounded-lg bg-amber-700 text-white text-[13px] font-medium hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
                                {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                                I accept v{status.current_version}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
